import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const HomePage = () => {
  const statsRef = useRef<HTMLDivElement>(null);
  const [statsVisible, setStatsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStatsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const stats = [
    { value: 15, suffix: '+', label: 'Years Experience' },
    { value: 500, suffix: '+', label: 'Protected Sites' },
    { value: 24, suffix: '/7', label: 'Support Available' },
    { value: 99, suffix: '%', label: 'Client Satisfaction' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 lg:px-8 overflow-hidden bg-gradient-to-b from-white to-[#f7fafc]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div>
              <p className="text-xs font-semibold text-[#718096] tracking-wider uppercase mb-4">
                PROFESSIONAL SECURITY SOLUTIONS
              </p>
              <h1 className="text-5xl lg:text-6xl font-bold text-[#1a2b4a] mb-6 leading-tight">
                Protect What Matters Most
              </h1>
              <p className="text-base text-gray-600 mb-8 leading-relaxed">
                Advanced CCTV systems, access control, and comprehensive IT infrastructure solutions designed to keep your business secure and connected. Expert installation and 24/7 monitoring services.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-12">
                <Link
                  to="/contact#consultation"
                  className="bg-[#1a2b4a] text-white px-8 py-3.5 rounded-lg text-sm font-medium hover:bg-[#2a3b5a] transition-all whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl"
                >
                  Schedule Consultation
                </Link>
                <Link
                  to="/services"
                  className="bg-white text-[#1a2b4a] px-8 py-3.5 rounded-lg text-sm font-medium hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer border-2 border-[#1a2b4a]"
                >
                  Explore Services
                </Link>
              </div>

              {/* Trust Indicators */}
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <div className="text-3xl font-bold text-[#1a2b4a] mb-1">500+</div>
                  <div className="text-xs text-gray-600">Sites Protected</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-[#1a2b4a] mb-1">99.9%</div>
                  <div className="text-xs text-gray-600">Uptime Rate</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-[#1a2b4a] mb-1">24/7</div>
                  <div className="text-xs text-gray-600">Support</div>
                </div>
              </div>
            </div>

            {/* Right Video Section */}
            <div className="relative">
              {/* Main Video Container */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-auto"
                >
                  <source src="https://ui.com/microsite/static/physical-security-1-Cz6S4fvO.mp4" type="video/mp4" />
                </video>
                
                {/* Live Badge */}
                <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-2">
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                  LIVE
                </div>

                {/* Camera Info Overlay */}
                <div className="absolute -bottom-8 left-4 right-4 bg-white/95 backdrop-blur-sm rounded-lg p-4 shadow-lg z-10">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="w-8 h-8 bg-[#e6f2ff] rounded-lg flex items-center justify-center mx-auto mb-2">
                        <i className="ri-hd-line text-[#1a2b4a]"></i>
                      </div>
                      <div className="text-xs font-semibold text-[#1a2b4a]">4K Quality</div>
                    </div>
                    <div>
                      <div className="w-8 h-8 bg-[#e6f2ff] rounded-lg flex items-center justify-center mx-auto mb-2">
                        <i className="ri-moon-line text-[#1a2b4a]"></i>
                      </div>
                      <div className="text-xs font-semibold text-[#1a2b4a]">Night Vision</div>
                    </div>
                    <div>
                      <div className="w-8 h-8 bg-[#e6f2ff] rounded-lg flex items-center justify-center mx-auto mb-2">
                        <i className="ri-cloud-line text-[#1a2b4a]"></i>
                      </div>
                      <div className="text-xs font-semibold text-[#1a2b4a]">Cloud Storage</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative Elements */}
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-[#14B8A6]/10 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#1a2b4a]/10 rounded-full blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section ref={statsRef} className="py-16 bg-[#1a2b4a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                  <span className={`inline-block transition-all duration-1000 ${statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: `${index * 150}ms` }}>
                    {stat.value}
                  </span>
                  <span className="text-[#0ea5e9]">{stat.suffix}</span>
                </div>
                <p className="text-gray-400 text-sm font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block text-xs font-semibold text-[#0ea5e9] tracking-wider uppercase mb-4 bg-[#e8f4fd] px-4 py-2 rounded-full">
              Our Services
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a2b4a] mb-4">
              Comprehensive Security &amp; IT Solutions
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From surveillance to network management, we provide end-to-end security and IT systems tailored to your needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* CCTV Systems */}
            <div className="group relative bg-white rounded-2xl p-8 border border-gray-100 hover:border-[#0ea5e9]/30 hover:shadow-xl transition-all duration-500 cursor-pointer overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#e8f4fd] to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-[#e8f4fd] to-[#d1e9fa] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-camera-line text-2xl text-[#0ea5e9]"></i>
                </div>
                <h3 className="text-xl font-bold text-[#1a2b4a] mb-3">CCTV Systems</h3>
                <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                  High-definition 4K surveillance with 24/7 remote monitoring, cloud storage, and intelligent motion detection.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-sm text-gray-600">
                    <i className="ri-check-line text-emerald-500 mr-2 w-4 h-4 flex items-center justify-center"></i>
                    4K Ultra HD Cameras
                  </li>
                  <li className="flex items-center text-sm text-gray-600">
                    <i className="ri-check-line text-emerald-500 mr-2 w-4 h-4 flex items-center justify-center"></i>
                    Night Vision Technology
                  </li>
                  <li className="flex items-center text-sm text-gray-600">
                    <i className="ri-check-line text-emerald-500 mr-2 w-4 h-4 flex items-center justify-center"></i>
                    Smart Alert System
                  </li>
                </ul>
                <Link
                  to="/services#cctv"
                  className="inline-flex items-center text-sm font-semibold text-[#0ea5e9] hover:text-[#1a2b4a] transition-colors cursor-pointer"
                >
                  Learn More
                  <i className="ri-arrow-right-line ml-1 w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform"></i>
                </Link>
              </div>
            </div>

            {/* Access Control */}
            <div className="group relative bg-white rounded-2xl p-8 border border-gray-100 hover:border-[#0ea5e9]/30 hover:shadow-xl transition-all duration-500 cursor-pointer overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#e8f4fd] to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-[#e8f4fd] to-[#d1e9fa] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-door-lock-line text-2xl text-[#0ea5e9]"></i>
                </div>
                <h3 className="text-xl font-bold text-[#1a2b4a] mb-3">Access Control</h3>
                <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                  Keycard and biometric entry systems with multi-level authorization and real-time access logging.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-sm text-gray-600">
                    <i className="ri-check-line text-emerald-500 mr-2 w-4 h-4 flex items-center justify-center"></i>
                    Biometric Authentication
                  </li>
                  <li className="flex items-center text-sm text-gray-600">
                    <i className="ri-check-line text-emerald-500 mr-2 w-4 h-4 flex items-center justify-center"></i>
                    Multi-Level Access
                  </li>
                  <li className="flex items-center text-sm text-gray-600">
                    <i className="ri-check-line text-emerald-500 mr-2 w-4 h-4 flex items-center justify-center"></i>
                    Real-Time Logs
                  </li>
                </ul>
                <Link
                  to="/services#access"
                  className="inline-flex items-center text-sm font-semibold text-[#0ea5e9] hover:text-[#1a2b4a] transition-colors cursor-pointer"
                >
                  Learn More
                  <i className="ri-arrow-right-line ml-1 w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform"></i>
                </Link>
              </div>
            </div>

            {/* Building Security */}
            <div className="group relative bg-white rounded-2xl p-8 border border-gray-100 hover:border-[#0ea5e9]/30 hover:shadow-xl transition-all duration-500 cursor-pointer overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#e8f4fd] to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-[#e8f4fd] to-[#d1e9fa] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-building-line text-2xl text-[#0ea5e9]"></i>
                </div>
                <h3 className="text-xl font-bold text-[#1a2b4a] mb-3">Building Security</h3>
                <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                  Complete perimeter protection with intrusion detection, fire alarms, and emergency response systems.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-sm text-gray-600">
                    <i className="ri-check-line text-emerald-500 mr-2 w-4 h-4 flex items-center justify-center"></i>
                    Perimeter Protection
                  </li>
                  <li className="flex items-center text-sm text-gray-600">
                    <i className="ri-check-line text-emerald-500 mr-2 w-4 h-4 flex items-center justify-center"></i>
                    Fire &amp; Emergency Systems
                  </li>
                  <li className="flex items-center text-sm text-gray-600">
                    <i className="ri-check-line text-emerald-500 mr-2 w-4 h-4 flex items-center justify-center"></i>
                    Integrated Management
                  </li>
                </ul>
                <Link
                  to="/services#building"
                  className="inline-flex items-center text-sm font-semibold text-[#0ea5e9] hover:text-[#1a2b4a] transition-colors cursor-pointer"
                >
                  Learn More
                  <i className="ri-arrow-right-line ml-1 w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform"></i>
                </Link>
              </div>
            </div>

            {/* Network & Security Management */}
            <div className="group relative bg-white rounded-2xl p-8 border border-gray-100 hover:border-[#0ea5e9]/30 hover:shadow-xl transition-all duration-500 cursor-pointer overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#e8f4fd] to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-[#e8f4fd] to-[#d1e9fa] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-shield-keyhole-line text-2xl text-[#0ea5e9]"></i>
                </div>
                <h3 className="text-xl font-bold text-[#1a2b4a] mb-3">Network &amp; Security</h3>
                <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                  Expert network infrastructure management with advanced security protocols and continuous monitoring.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-sm text-gray-600">
                    <i className="ri-check-line text-emerald-500 mr-2 w-4 h-4 flex items-center justify-center"></i>
                    Network Infrastructure
                  </li>
                  <li className="flex items-center text-sm text-gray-600">
                    <i className="ri-check-line text-emerald-500 mr-2 w-4 h-4 flex items-center justify-center"></i>
                    Cybersecurity Solutions
                  </li>
                  <li className="flex items-center text-sm text-gray-600">
                    <i className="ri-check-line text-emerald-500 mr-2 w-4 h-4 flex items-center justify-center"></i>
                    24/7 Network Monitoring
                  </li>
                </ul>
                <Link
                  to="/services#network"
                  className="inline-flex items-center text-sm font-semibold text-[#0ea5e9] hover:text-[#1a2b4a] transition-colors cursor-pointer"
                >
                  Learn More
                  <i className="ri-arrow-right-line ml-1 w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform"></i>
                </Link>
              </div>
            </div>

            {/* Cloud Solutions */}
            <div className="group relative bg-white rounded-2xl p-8 border border-gray-100 hover:border-[#0ea5e9]/30 hover:shadow-xl transition-all duration-500 cursor-pointer overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#e8f4fd] to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-[#e8f4fd] to-[#d1e9fa] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-cloud-line text-2xl text-[#0ea5e9]"></i>
                </div>
                <h3 className="text-xl font-bold text-[#1a2b4a] mb-3">Cloud Solutions</h3>
                <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                  Scalable cloud infrastructure and migration services to enhance flexibility and reduce costs.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-sm text-gray-600">
                    <i className="ri-check-line text-emerald-500 mr-2 w-4 h-4 flex items-center justify-center"></i>
                    Cloud Migration
                  </li>
                  <li className="flex items-center text-sm text-gray-600">
                    <i className="ri-check-line text-emerald-500 mr-2 w-4 h-4 flex items-center justify-center"></i>
                    Infrastructure Management
                  </li>
                  <li className="flex items-center text-sm text-gray-600">
                    <i className="ri-check-line text-emerald-500 mr-2 w-4 h-4 flex items-center justify-center"></i>
                    Data Backup &amp; Recovery
                  </li>
                </ul>
                <Link
                  to="/services#cloud"
                  className="inline-flex items-center text-sm font-semibold text-[#0ea5e9] hover:text-[#1a2b4a] transition-colors cursor-pointer"
                >
                  Learn More
                  <i className="ri-arrow-right-line ml-1 w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform"></i>
                </Link>
              </div>
            </div>

            {/* IT Infrastructure */}
            <div className="group relative bg-white rounded-2xl p-8 border border-gray-100 hover:border-[#0ea5e9]/30 hover:shadow-xl transition-all duration-500 cursor-pointer overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-[#e8f4fd] to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-br from-[#e8f4fd] to-[#d1e9fa] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-server-line text-2xl text-[#0ea5e9]"></i>
                </div>
                <h3 className="text-xl font-bold text-[#1a2b4a] mb-3">IT Infrastructure</h3>
                <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                  Optimize your IT infrastructure for maximum performance, reliability, and productivity.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-sm text-gray-600">
                    <i className="ri-check-line text-emerald-500 mr-2 w-4 h-4 flex items-center justify-center"></i>
                    Infrastructure Optimization
                  </li>
                  <li className="flex items-center text-sm text-gray-600">
                    <i className="ri-check-line text-emerald-500 mr-2 w-4 h-4 flex items-center justify-center"></i>
                    Remote Working Solutions
                  </li>
                  <li className="flex items-center text-sm text-gray-600">
                    <i className="ri-check-line text-emerald-500 mr-2 w-4 h-4 flex items-center justify-center"></i>
                    Digital Transformation
                  </li>
                </ul>
                <Link
                  to="/services#infrastructure"
                  className="inline-flex items-center text-sm font-semibold text-[#0ea5e9] hover:text-[#1a2b4a] transition-colors cursor-pointer"
                >
                  Learn More
                  <i className="ri-arrow-right-line ml-1 w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-6 lg:px-8 bg-[#f8fafc]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left - Image Grid */}
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="rounded-2xl overflow-hidden shadow-lg">
                    <img
                      src="https://static.readdy.ai/image/5599338006c54fe6ce07ff64cfd8f48c/56e0d0aa75108782e4cb3c6f53661369.png"
                      alt="Professional Installation"
                      className="w-full h-48 object-cover object-top"
                    />
                  </div>
                  <div className="rounded-2xl overflow-hidden shadow-lg">
                    <img
                      src="https://static.readdy.ai/image/5599338006c54fe6ce07ff64cfd8f48c/591faeff9964dd6e3ca1513937d4504d.png"
                      alt="Access Control"
                      className="w-full h-56 object-cover object-top"
                    />
                  </div>
                </div>
                <div className="space-y-4 pt-8">
                  <div className="rounded-2xl overflow-hidden shadow-lg">
                    <img
                      src="https://public.readdy.ai/ai/img_res/b09646309652db7e024508f2301f4bf1.jpg"
                      alt="24/7 Monitoring"
                      className="w-full h-56 object-cover object-top"
                    />
                  </div>
                  <div className="rounded-2xl overflow-hidden shadow-lg">
                    <img
                      src="https://static.readdy.ai/image/5599338006c54fe6ce07ff64cfd8f48c/bf0d2b561602b16717ccd9349b16ab1c.png"
                      alt="Modern Equipment"
                      className="w-full h-48 object-cover object-top"
                    />
                  </div>
                </div>
              </div>
              
              {/* Floating Badge */}
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-[#1a2b4a] text-white px-6 py-3 rounded-full shadow-xl flex items-center gap-3">
                <i className="ri-award-line text-[#0ea5e9] text-xl"></i>
                <span className="text-sm font-semibold whitespace-nowrap">Certified Professionals</span>
              </div>
            </div>

            {/* Right - Content */}
            <div>
              <span className="inline-block text-xs font-semibold text-[#0ea5e9] tracking-wider uppercase mb-4">
                Why Choose Us
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a2b4a] mb-6">
                Your Trusted Security Partner
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                With over 15 years of experience, we've built a reputation for excellence in security solutions. Our team of certified professionals delivers customized systems that protect what matters most.
              </p>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-[#e8f4fd] rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-customer-service-2-line text-[#0ea5e9] text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1a2b4a] mb-1">24/7 Expert Support</h3>
                    <p className="text-sm text-gray-600">Round-the-clock technical support and emergency response services.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-[#e8f4fd] rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-tools-line text-[#0ea5e9] text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1a2b4a] mb-1">Professional Installation</h3>
                    <p className="text-sm text-gray-600">Certified technicians ensure flawless setup and integration.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-[#e8f4fd] rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-star-line text-[#0ea5e9] text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1a2b4a] mb-1">Warranty & Maintenance</h3>
                    <p className="text-sm text-gray-600">Comprehensive warranty coverage with regular maintenance plans.</p>
                  </div>
                </div>
              </div>

              <Link
                to="/tour"
                className="inline-flex items-center gap-2 mt-8 text-sm font-semibold text-[#1a2b4a] hover:text-[#0ea5e9] transition-colors cursor-pointer"
              >
                Take a Solutions Tour
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Features */}
      <section className="py-20 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block text-xs font-semibold text-[#0ea5e9] tracking-wider uppercase mb-4">
              Advanced Technology
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a2b4a] mb-4">
              Cutting-Edge Security Features
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: 'ri-robot-line', title: 'AI Monitoring', desc: 'Smart threat detection' },
              { icon: 'ri-cloud-line', title: 'Cloud Storage', desc: 'Secure data backup' },
              { icon: 'ri-smartphone-line', title: 'Mobile Access', desc: 'Control anywhere' },
              { icon: 'ri-notification-3-line', title: 'Real-Time Alerts', desc: 'Instant notifications' },
              { icon: 'ri-fingerprint-line', title: 'Biometric Auth', desc: 'Advanced security' },
              { icon: 'ri-wifi-line', title: 'Wireless Systems', desc: 'Flexible installation' },
              { icon: 'ri-moon-line', title: 'Night Vision', desc: '24/7 visibility' },
              { icon: 'ri-database-2-line', title: 'Data Analytics', desc: 'Insights & reports' },
            ].map((feature, index) => (
              <div
                key={index}
                className="group bg-[#f8fafc] hover:bg-[#1a2b4a] rounded-2xl p-6 text-center transition-all duration-300 cursor-pointer"
              >
                <div className="w-14 h-14 bg-white group-hover:bg-white/10 rounded-xl flex items-center justify-center mx-auto mb-4 transition-colors duration-300">
                  <i className={`${feature.icon} text-2xl text-[#0ea5e9] group-hover:text-[#0ea5e9]`}></i>
                </div>
                <h3 className="font-semibold text-[#1a2b4a] group-hover:text-white mb-1 transition-colors duration-300">
                  {feature.title}
                </h3>
                <p className="text-xs text-gray-500 group-hover:text-gray-300 transition-colors duration-300">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-[#1a2b4a] to-[#2d4a6f] relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 border border-white rounded-full"></div>
          <div className="absolute bottom-10 right-10 w-48 h-48 border border-white/30 rounded-full"></div>
          <div className="absolute top-1/2 left-1/3 w-24 h-24 border border-white/30 rounded-full"></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full mb-6">
            <i className="ri-shield-check-line text-[#0ea5e9]"></i>
            <span className="text-xs font-semibold text-white tracking-wide uppercase">
              Free Security Assessment
            </span>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Secure Your Property?
          </h2>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            Get a comprehensive security assessment and customized solution proposal. Our experts will analyze your needs and recommend the perfect system for your property.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contact#consultation"
              className="bg-white text-[#1a2b4a] px-8 py-4 rounded-lg text-sm font-semibold hover:bg-[#0ea5e9] hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
            >
              Schedule Consultation
              <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
            </Link>
            <Link
              to="/contact"
              className="bg-transparent text-white px-8 py-4 rounded-lg text-sm font-semibold border-2 border-white/30 hover:border-white hover:bg-white/10 transition-all duration-300 whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
            >
              <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
              Contact Us
            </Link>
          </div>
          
          <p className="text-gray-400 text-xs mt-6">
            No obligation • Response within 24 hours • Free on-site evaluation
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
